import moment from 'moment';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';

const ChaveCell = ({ value }) => {
  const handleCopy = async (event) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(value));
    } catch (error) {
      console.error('Erro ao copiar chave:', error);
    }
  };

  if (!value) return null;

  return (
    <Box display="flex" alignItems="flex-start" flex={2} minWidth={0} gap={0.5}>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          overflowWrap: 'anywhere',
          wordBreak: 'break-all',
          lineHeight: 1.4,
        }}
      >
        {value}
      </Typography>
      <Tooltip title="Copiar chave" disableInteractive>
        <IconButton
          size="small"
          onClick={handleCopy}
          onMouseDown={(event) => event.stopPropagation()}
          aria-label="Copiar chave"
          sx={{ flexShrink: 0, padding: '2px' }}
        >
          <ContentCopy sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const EntidadeCell = ({ row }) => (
  <Box flex={1}>
    <Typography variant="body2" fontWeight="bold" mb={1}>
      {row?.rsocial}
    </Typography>
    {Boolean(row?.cnpj) && (
      <Box display="flex" alignItems="center">
        <Typography variant="body2" fontWeight="bold" flex={1}>
          CNPJ:{' '}
        </Typography>
        <Typography variant="body2" flex={3}>
          {row?.cnpj
            ?.replace(/\D/g, '')
            ?.replace(/^(\d{2})(\d)/, '$1.$2')
            ?.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
            ?.replace(/\.(\d{3})(\d)/, '.$1/$2')
            ?.replace(/(\d{4})(\d)/, '$1-$2')
            ?.replace(/(-\d{2})\d+?$/, '$1')}
        </Typography>
      </Box>
    )}
    {Boolean(row?.ie) && (
      <Box display="flex" alignItems="center">
        <Typography variant="body2" fontWeight="bold" flex={1}>
          IE:{' '}
        </Typography>
        <Typography variant="body2" flex={3}>
          {row?.ie}
        </Typography>
      </Box>
    )}
  </Box>
);

const DocumentoCell = ({ row }) => (
  <Box flex={1}>
    <Box mb={1} display="flex" alignItems="center" justifyContent="space-between">
      <Typography variant="body2" fontWeight="bold">
        {row?.tpdocto || ''} {row?.numero || ''}
      </Typography>
      {Boolean(row?.status) && (
        <Chip
          label={row?.status}
          color={chipColor[row?.status] || 'primary'}
          size="small"
        />
      )}
    </Box>
    {Boolean(row?.operacao) && (
      <Box display="flex">
        <Typography variant="body2" fontWeight="bold" flex={1}>
          Operação:{' '}
        </Typography>
        <Typography variant="body2" flex={2}>
          {row?.operacao}
        </Typography>
      </Box>
    )}
    {Boolean(row?.valor) && (
      <Box display="flex">
        <Typography variant="body2" fontWeight="bold" flex={1}>
          Valor:{' '}
        </Typography>
        <Typography variant="body2" flex={2}>
          {row?.valor?.toLocaleString('pt-br', {
            style: 'currency',
            currency: 'BRL',
          })}
        </Typography>
      </Box>
    )}
    {Boolean(row?.serie) && (
      <Box display="flex">
        <Typography variant="body2" fontWeight="bold" flex={1}>
          Série:{' '}
        </Typography>
        <Typography variant="body2" flex={2}>
          {row?.serie}
        </Typography>
      </Box>
    )}
    {Boolean(row?.chdfe) && (
      <Box display="flex">
        <Typography variant="body2" fontWeight="bold" flex={1}>
          Chave:{' '}
        </Typography>
        <ChaveCell value={row.chdfe} />
      </Box>
    )}
  </Box>
);

const SituacaoCell = ({ row }) => (
  <Box flex={1} alignItems="center" flexDirection="column" display="flex">
    <Chip
      size="small"
      color={situacaoColor[row?.situacao]}
      label={situacaoLabel[row?.situacao] || '-'}
    />
    <Chip
      size="small"
      variant="outlined"
      sx={{ mt: 1 }}
      color={xmlColor[row?.xml]}
      label={xmlLabel[row?.xml] || '-'}
    />
  </Box>
);

const chipColor = {
  AUTORIZADO: 'success',
  DENEGADO: 'error',
  CANCELADO: 'warning',
};
const situacaoColor = {
  210210: 'info',
  210200: 'success',
  210220: 'error',
  610110: 'error',
  210240: 'warning',
  0: 'default',
};
const situacaoLabel = {
  210210: 'Ciência da Operação',
  210200: 'Confirmação da Operação',
  210220: 'Desconhecimento da Operação',
  610110: 'Desacordo da Operação',
  210240: 'Operação não Realizada',
  0: 'Não Manifestado',
};
const xmlColor = {
  IMPORTAR: 'info',
  IMPORTADO: 'success',
  INDISPONIVEL: 'error',
};
const xmlLabel = {
  IMPORTAR: 'XML Disponível',
  IMPORTADO: 'XML Importado',
  INDISPONIVEL: 'XML Indisponível',
};

const columnOverrides = [
  { field: 'situacao', renderCell: ({ row }) => <SituacaoCell row={row} /> },
  { field: 'emitente', renderCell: ({ row }) => <EntidadeCell row={row} /> },
  { field: 'nf', renderCell: ({ row }) => <DocumentoCell row={row} /> },
  {
    field: 'dhemissao',
    valueGetter: ({ value }) =>
      value && moment(value).format('DD/MM/YYYY [às] HH:mm'),
  },
];

export default columnOverrides;
